import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Reformity brand button — sharp edges, uppercase Montserrat, three variants.
 *
 *   variant: "primary" (coral) | "secondary" (cyan) | "outline" (transparent)
 *   size:    "md" | "lg"
 *   tone:    "onLight" | "onDark"  (controls outline color contrast)
 */
const brandButtonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans font-semibold uppercase tracking-wider transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-cyan disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                primary:
                    "bg-brand-coral text-white hover:bg-[#d04438] active:translate-y-px",
                secondary:
                    "bg-brand-cyan text-white hover:bg-[#2fa9b8] active:translate-y-px",
                outline:
                    "bg-transparent border-2 border-current hover:bg-white/10 active:translate-y-px",
            },
            tone: {
                onLight: "",
                onDark: "",
            },
            size: {
                md: "h-11 px-5 text-xs",
                lg: "h-14 px-7 text-sm",
            },
        },
        compoundVariants: [
            {
                variant: "outline",
                tone: "onLight",
                class: "text-brand-dark border-brand-dark hover:bg-brand-dark hover:text-white",
            },
            {
                variant: "outline",
                tone: "onDark",
                class: "text-white border-white hover:bg-white hover:text-brand-dark",
            },
        ],
        defaultVariants: {
            variant: "primary",
            size: "md",
            tone: "onLight",
        },
    },
);

const BrandButton = React.forwardRef(
    (
        { className, variant, size, tone, asChild = false, ...props },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                ref={ref}
                className={cn(brandButtonVariants({ variant, size, tone, className }))}
                {...props}
            />
        );
    },
);
BrandButton.displayName = "BrandButton";

export { BrandButton, brandButtonVariants };
